import React, { useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { Trash2, Pencil, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import firebase from '../db/firebase/firebase';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [title, setTitle] = useState('');
  const [points, setPoints] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [text, setText] = useState('');
  const [hasLink, setHasLink] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [url, setUrl] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState('');

  const fetchProducts = async () => {
    const snapshot = await getDocs(collection(firebase.db, 'productsShop'));
    setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const resetForm = () => {
    setTitle('');
    setPoints('');
    setImageFile(null);
    setImageUrl('');
    setText('');
    setHasLink(false);
    setLinkText('');
    setUrl('');
    setEditingId(null);
    setError('');
  };

  const handleSubmit = async () => {
    if (!title || !points || (!imageUrl && !imageFile) || !text) {
      setError('Please complete all required fields.');
      return;
    }

    setLoading(true);

    try {
      let finalImageUrl = imageUrl;
      if (imageFile) {
        const storageRef = ref(firebase.storage, `productsShop/${Date.now()}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        finalImageUrl = await getDownloadURL(storageRef);
      }

      const productData = {
        title,
        points,
        imgUrl: finalImageUrl,
        text,
        hasLink: !!hasLink,
        linkText: hasLink ? linkText : '',
        url: hasLink ? url : '',
      };

      if (editingId) {
        await updateDoc(doc(firebase.db, 'productsShop', editingId), productData);
      } else {
        await addDoc(collection(firebase.db, 'productsShop'), productData);
      }

      resetForm();
      setModalOpen(false);
      fetchProducts();
    } catch (err) {
      console.error('Error saving product:', err);
    }

    setLoading(false);
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setTitle(product.title);
    setPoints(product.points);
    setImageUrl(product.imgUrl);
    setText(product.text);
    setHasLink(!!product.hasLink);
    setLinkText(product.linkText || '');
    setUrl(product.url || '');
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await deleteDoc(doc(firebase.db, 'productsShop', id));
      fetchProducts();
    }
  };

  return (
    <div className=" flex flex-col h-screen p-5 flex-1 bg-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Products</h1>
        <button
          onClick={() => {
            resetForm();
            setModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700"
        >
          <Plus size={18} /> Add Product
        </button>
      </div>
      <div className="flex-1 overflow-y-auto pr-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {products.map((product) => (
      <motion.div
        key={product.id}
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
        className="bg-white shadow rounded-xl p-4 relative"
      >
            <button
              onClick={() => handleDelete(product.id)}
              className="absolute top-3 right-3 text-red-600 hover:text-red-800"
            >
              <Trash2 size={20} />
            </button>
            <button
              onClick={() => handleEdit(product)}
              className="absolute top-3 left-3 text-yellow-600 hover:text-yellow-800"
            >
              <Pencil size={18} />
            </button>
            <img
              src={product.imgUrl}
              alt={product.title}
              className="w-full h-40 object-contain rounded mb-4"
            />
            <h2 className="text-lg font-semibold mb-2">{product.title}</h2>
            <p className="text-sm text-gray-500 mb-1">Points: {product.points}</p>
            <p className="text-sm text-gray-700 whitespace-pre-line mb-2">{product.text}</p>
            {product.hasLink && (
              <a href={product.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                {product.linkText}
              </a>
            )}
          </motion.div>
        ))}
      </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative"
          >
            <h2 className="text-xl font-semibold mb-4">{editingId ? 'Edit Product' : 'Add Product'}</h2>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <input
              type="text"
              placeholder="Title"
              className="w-full border rounded px-3 py-2 mb-3"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <input
              type="number"
              placeholder="Points"
              className="w-full border rounded px-3 py-2 mb-3"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
            />
            <input
              type="file"
              className="w-full border rounded px-3 py-2 mb-3"
              onChange={(e) => setImageFile(e.target.files[0])}
            />
            <textarea
              rows={4}
              placeholder="Enter product features. Use \n for new lines."
              className="w-full border rounded px-3 py-2 mb-3"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={hasLink}
                onChange={() => setHasLink(!hasLink)}
              />
              Has link
            </label>
            {hasLink && (
              <>
                <input
                  type="text"
                  placeholder="Link Text"
                  className="w-full border rounded px-3 py-2 mb-3"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="URL"
                  className="w-full border rounded px-3 py-2 mb-3"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              </>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setModalOpen(false);
                  resetForm();
                }}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Products;